import { BodyLong, Heading, Link, VerticalSpace, HorizontalSpace } from '@elhub/ds-components'
import React from 'react'
import { Link as NavLink } from 'react-router-dom'
import tokens from '@elhub/ds-tokens/dist/tokens.json'

const DatasetUsageGuidelines = () => {
  return (
    <>
      <Heading>Usage Guidelines</Heading>
      <VerticalSpace />
      <BodyLong size='small'>
        <b>Date Range Limitations:</b>
        <VerticalSpace />
        <ol>
          <li>
            <b><code>startDate</code> and <code>endDate</code> must follow the ISO 8601 format:</b>
            <ul>
              <VerticalSpace />
              <li>
                <b>Basic format:</b> <code>"yyyy-MM-dd"</code> (defaults to midnight).
              </li>
              <VerticalSpace />
              <li>
                <b>Full format with timezone offset:</b> <code>"yyyy-MM-dd'T'HH:mm:ssXXX"</code> (must be URL-encoded, e.g., <code>2023-01-01T00:00:00%2B02:00</code>).
              </li>
            </ul>
            <VerticalSpace />
          </li>
          <li>
            <b>Behavior:</b>
            <ul>
              <VerticalSpace />
              <li>
                Both <code>startDate</code> and <code>endDate</code> are inclusive in the response.
              </li>
              <VerticalSpace />
              <li>
                If <code>endDate</code> is omitted, it defaults to the current date or the maximum allowed range after <code>startDate</code>.
              </li>
              <VerticalSpace />
              <li>
                If <code>startDate</code> is omitted, it defaults to the earliest allowed date within the dataset's range.
              </li>
              <VerticalSpace />
              <li>
                If both are omitted, the API automatically sets:
                <ul>
                  <li>
                    <code>endDate = current time</code>
                  </li>
                  <li>
                    <code>startDate = endDate - max allowed range (day/week/month, depending on the dataset)</code>
                  </li>
                </ul>
              </li>
              <VerticalSpace />
            </ul>
          </li>
          <li>
            <b>Each dataset has a maximum allowed date range</b>, which varies based on dataset size.
            <ul>
              <VerticalSpace />
              <li>
                Example: If a dataset has a <b>1-week limit</b>, queries cannot exceed <b>7 days per request</b>.
              </li>
              <VerticalSpace />
              <li>
                Refer to the dataset table for specific filtering rules.
              </li>
            </ul>
            <VerticalSpace />
          </li>
          <li>
            <b>All dataset exports do not have date filters. The API would return the data for the last 3 years.</b>
            <VerticalSpace />
          </li>
        </ol>
        <VerticalSpace />
      </BodyLong>
      <VerticalSpace />
      <BodyLong size='small'>
        <b>Access Control for Protected Datasets:</b>
        <VerticalSpace />
        <ol>
          <li>
            All datasets with the suffix <b>"_RESTRICTED"</b> contain business-sensitive / private data and require Maskinporten Authentication.
            <VerticalSpace />
          </li>
          <li>
            <b>Only consumers assigned the relevant scope(s) in Maskinporten by Elhub can access protected datasets.</b>
            <VerticalSpace />
          </li>
          <li>
            Read more{' '}
            <Link as={NavLink} to='https://api.elhub.no/maskinporten/create-client'>
              here
            </Link>
            <HorizontalSpace size='0.2' />
            once you have been assigned the necessary scope(s).
            <VerticalSpace />
          </li>
          <li>
            When accessing a <b>protected dataset</b>, include the following HTTP header:
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', backgroundColor: tokens['eds-color-grey-lighter90'], padding: '1rem' }}>
              <code>Authorization: Bearer {'{MASKINPORTEN_ACCESS_TOKEN}'}</code>
              <VerticalSpace />
              <code>X-Elhub-GLN: {'{YOUR_GLN_VALUE}'}</code>
            </pre>
            <VerticalSpace />
          </li>
        </ol>
        <VerticalSpace />
      </BodyLong>
    </>
  )
}

export default DatasetUsageGuidelines
